import { z } from 'zod';

// ---------------------------------------------------------------------------
// Period format regex (YYYY-MM)
// ---------------------------------------------------------------------------

const periodoRegex = /^\d{4}-(0[1-9]|1[0-2])$/;

// ---------------------------------------------------------------------------
// Semaforo (traffic light) status
// ---------------------------------------------------------------------------

export const SemaforoStatusSchema = z.enum(['positivo', 'precaucion', 'negativo', 'sin_meta']);
export type SemaforoStatus = z.infer<typeof SemaforoStatusSchema>;

// ---------------------------------------------------------------------------
// KPI names (the 5 legacy KPIs)
// ---------------------------------------------------------------------------

export const KPI_NAMES = {
  DISPONIBILIDAD: 'Disponibilidad',
  CUMPLIMIENTO_PLANIFICADO: 'Cumplimiento Trabajo Planificado',
  CUMPLIMIENTO_PM: 'Cumplimiento Plan Matriz',
  TRABAJO_EQUIPO: 'Trabajo sobre Equipo',
  HORAS_NOTIFICADAS: 'Horas Notificadas Totales',
  TASA_CUMPLIMIENTO: 'Tasa Cumplimiento OTs',
  TASA_RECHAZO: 'Tasa Rechazo',
  TASA_NO_EJECUTADO: 'Tasa No Ejecutado',
  TASA_REPROGRAMACION: 'Tasa Reprogramacion',
  BACKLOG: 'Backlog Mantenimiento',
} as const;

export type KpiName = (typeof KPI_NAMES)[keyof typeof KPI_NAMES];

// ---------------------------------------------------------------------------
// KPI query (GET /kpi?ambitoId=...&periodo=...)
// ---------------------------------------------------------------------------

export const KpiQuerySchema = z.object({
  ambitoId: z.coerce.number().int().positive(),
  periodo: z.string().regex(periodoRegex, 'Formato: YYYY-MM'),
});
export type KpiQuery = z.infer<typeof KpiQuerySchema>;

// ---------------------------------------------------------------------------
// KPI trends query (GET /kpi/trends?ambitoId=...&periodos=...)
// ---------------------------------------------------------------------------

export const KpiTrendsQuerySchema = z.object({
  ambitoId: z.coerce.number().int().positive(),
  periodos: z.string()
    .transform((s) => s.split(',').map((p) => p.trim()))
    .pipe(z.array(z.string().regex(periodoRegex, 'Formato: YYYY-MM')).min(1).max(24)),
});
export type KpiTrendsQuery = z.infer<typeof KpiTrendsQuerySchema>;

// ---------------------------------------------------------------------------
// KPI metas query (GET /kpi/metas?ambitoId=...)
// ---------------------------------------------------------------------------

export const KpiMetasQuerySchema = z.object({
  ambitoId: z.coerce.number().int().positive(),
});
export type KpiMetasQuery = z.infer<typeof KpiMetasQuerySchema>;

// ---------------------------------------------------------------------------
// Meta range (min/max target values)
// ---------------------------------------------------------------------------

export const MetaRangeSchema = z.object({
  minimo: z.number(),
  maximo: z.number(),
});
export type MetaRange = z.infer<typeof MetaRangeSchema>;

// ---------------------------------------------------------------------------
// KPI result (single KPI calculation result)
// ---------------------------------------------------------------------------

export const KpiResultSchema = z.object({
  kpiId: z.number(),
  nombre: z.string(),
  valor: z.number(),
  exigido: z.number().nullable(),
  cumplimiento: z.number().nullable(),
  semaforo: SemaforoStatusSchema,
  meta: MetaRangeSchema.nullable(),
});
export type KpiResult = z.infer<typeof KpiResultSchema>;

// ---------------------------------------------------------------------------
// KPI response (all KPIs for a given ambito+periodo)
// ---------------------------------------------------------------------------

export const KpiResponseSchema = z.object({
  ambitoId: z.number(),
  periodo: z.string(),
  kpis: z.array(KpiResultSchema),
  calculatedAt: z.string(),
  warnings: z.array(z.string()).optional(),
  partial: z.boolean().optional(),
});
export type KpiResponse = z.infer<typeof KpiResponseSchema>;

// ---------------------------------------------------------------------------
// KPI trend point (single period value for trend charts)
// ---------------------------------------------------------------------------

export const KpiTrendPointSchema = z.object({
  periodo: z.string(),
  valor: z.number(),
  exigido: z.number().nullable(),
  cumplimiento: z.number().nullable(),
  semaforo: SemaforoStatusSchema,
});
export type KpiTrendPoint = z.infer<typeof KpiTrendPointSchema>;

// ---------------------------------------------------------------------------
// KPI trend (multi-period data for a single KPI)
// ---------------------------------------------------------------------------

export const KpiTrendSchema = z.object({
  kpiId: z.number(),
  nombre: z.string(),
  meta: MetaRangeSchema.nullable(),
  points: z.array(KpiTrendPointSchema),
});
export type KpiTrend = z.infer<typeof KpiTrendSchema>;

// ---------------------------------------------------------------------------
// KPI trends response
// ---------------------------------------------------------------------------

export const KpiTrendsResponseSchema = z.object({
  ambitoId: z.number(),
  trends: z.array(KpiTrendSchema),
});
export type KpiTrendsResponse = z.infer<typeof KpiTrendsResponseSchema>;

// ---------------------------------------------------------------------------
// KPI meta response (active targets for all KPIs of an ambito)
// ---------------------------------------------------------------------------

export const KpiMetaResultSchema = z.object({
  kpiId: z.number(),
  nombre: z.string(),
  minimo: z.number(),
  maximo: z.number(),
  vigenciaDesde: z.string(),
  vigenciaHasta: z.string().nullable(),
});
export type KpiMetaResult = z.infer<typeof KpiMetaResultSchema>;

export const KpiMetasResponseSchema = z.object({
  ambitoId: z.number(),
  metas: z.array(KpiMetaResultSchema),
});
export type KpiMetasResponse = z.infer<typeof KpiMetasResponseSchema>;

// ---------------------------------------------------------------------------
// Available periods query (GET /kpi/periods?ambitoId=...)
// ---------------------------------------------------------------------------

export const KpiPeriodsQuerySchema = z.object({
  ambitoId: z.coerce.number().int().positive(),
});
export type KpiPeriodsQuery = z.infer<typeof KpiPeriodsQuerySchema>;

export const KpiPeriodsResponseSchema = z.object({
  ambitoId: z.number(),
  periods: z.array(z.string()),
});
export type KpiPeriodsResponse = z.infer<typeof KpiPeriodsResponseSchema>;

// ---------------------------------------------------------------------------
// Comparative query (GET /kpi/comparative?ambitoId=X&months=6)
// ---------------------------------------------------------------------------

export const KpiComparativeQuerySchema = z.object({
  ambitoId: z.coerce.number().int().positive(),
  months: z.coerce.number().int().min(1).max(24).default(6),
});
export type KpiComparativeQuery = z.infer<typeof KpiComparativeQuerySchema>;

/** A single KPI's values across multiple periods */
export const KpiComparativeRowSchema = z.object({
  kpiId: z.number(),
  nombre: z.string(),
  /** Map from periodo string to the computed value for that period */
  values: z.record(z.string(), z.object({
    valor: z.number(),
    cumplimiento: z.number().nullable(),
    semaforo: SemaforoStatusSchema,
  })),
});
export type KpiComparativeRow = z.infer<typeof KpiComparativeRowSchema>;

export const KpiComparativeResponseSchema = z.object({
  ambitoId: z.number(),
  periods: z.array(z.string()),
  rows: z.array(KpiComparativeRowSchema),
});
export type KpiComparativeResponse = z.infer<typeof KpiComparativeResponseSchema>;

// ---------------------------------------------------------------------------
// Backfill query (POST /kpi/backfill)
// ---------------------------------------------------------------------------

export const KpiBackfillBodySchema = z.object({
  ambitoId: z.number().int().positive(),
});
export type KpiBackfillBody = z.infer<typeof KpiBackfillBodySchema>;

export const KpiBackfillResponseSchema = z.object({
  ambitoId: z.number(),
  periodsProcessed: z.number(),
  periods: z.array(z.string()),
});
export type KpiBackfillResponse = z.infer<typeof KpiBackfillResponseSchema>;
